import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Users, Calendar, CheckCircle, XCircle, Percent, Download, UserPlus, Phone, FileText } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import {
  useSubjectClassDetails,
  useSubjectClassStudents,
  useClassAttendance,
  useMarkClassAttendance,
  useUpdateSubjectClass,
  useUpdateStudentProgress,
  useAddStudentsToClass,
} from "@/hooks/useSubjectClasses";
import { useUserRole } from "@/hooks/useUserRole";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import logoImage from "@/assets/skylink-logo.png";

const SubjectClassDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: roles } = useUserRole();
  const downloadRef = useRef<HTMLDivElement>(null);
  
  const isInstructor = roles?.isInstructor;
  const isManager = roles?.isManager && !roles?.isAdmin && !roles?.isSuperAdmin;
  const isAdminOrAbove = roles?.isAdmin || roles?.isSuperAdmin;
  const canMarkFullAttendance = isInstructor || isAdminOrAbove; // Managers can only mark absences
  const canMarkComplete = isInstructor; // Only instructors can mark teaching complete
  const canEditExamResults = isInstructor || isAdminOrAbove; // Managers cannot edit exam results
  
  const { data: classData, isLoading: classLoading } = useSubjectClassDetails(id || "");
  const { data: students, isLoading: studentsLoading } = useSubjectClassStudents(id || "");
  const { data: attendanceRecords } = useClassAttendance(id || "");
  
  const markAttendance = useMarkClassAttendance();
  const updateClass = useUpdateSubjectClass();
  const updateStudentProgress = useUpdateStudentProgress();
  const addStudentsToClass = useAddStudentsToClass();

  const [attendanceDate, setAttendanceDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [attendanceState, setAttendanceState] = useState<Record<string, string>>({});
  const [showMarkComplete, setShowMarkComplete] = useState(false);
  const [showAddStudents, setShowAddStudents] = useState(false);
  const [selectedNewStudents, setSelectedNewStudents] = useState<string[]>([]);
  const [attendanceMarkedSuccess, setAttendanceMarkedSuccess] = useState(false);
  const [showExamDateDialog, setShowExamDateDialog] = useState<string | null>(null);
  const [examApplicationDate, setExamApplicationDate] = useState("");

  // Fetch all students for adding to class
  const { data: allStudents } = useQuery({
    queryKey: ["all-students-for-class"],
    queryFn: async () => {
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, calling_name")
        .order("first_name");
      return data || [];
    },
  });

  // Fetch absence notes for this class
  const { data: absenceNotes } = useQuery({
    queryKey: ["class-absence-notes", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("student_absence_notes")
        .select(`
          *,
          student:students(first_name, last_name)
        `)
        .eq("subject_class_id", id)
        .order("absence_date", { ascending: false });
      return data || [];
    },
    enabled: !!id,
  });

  // Get students not already in the class
  const availableStudents = allStudents?.filter(
    student => !students?.some(s => s.student_id === student.id)
  );

  // Initialize attendance state - for managers, start with all present (they only mark absences)
  const initializeAttendance = () => {
    const initial: Record<string, string> = {};
    students?.forEach(s => {
      initial[s.student_id] = "Present";
    });
    setAttendanceState(initial);
  };

  const handleMarkAttendance = async () => {
    if (!id) return;

    const attendance = Object.entries(attendanceState).map(([student_id, status]) => ({
      student_id,
      status,
    }));

    try {
      await markAttendance.mutateAsync({
        classId: id,
        date: attendanceDate,
        attendance,
      });
      toast.success("Attendance marked successfully");
      setAttendanceMarkedSuccess(true);
    } catch (error) {
      toast.error("Failed to mark attendance");
    }
  };

  const handleDownloadAttendance = async () => {
    if (!downloadRef.current) return;

    try {
      // Make the download ref visible temporarily
      downloadRef.current.style.display = 'block';
      
      const canvas = await html2canvas(downloadRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
      });
      
      // Hide it again
      downloadRef.current.style.display = 'none';

      const link = document.createElement("a");
      link.download = `attendance-${classData?.class_name}-${attendanceDate}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast.success("Attendance downloaded!");
    } catch (error) {
      toast.error("Failed to download attendance");
    }
  };

  // Get attendance summary for download - uses calling_name first, fallback to first_name
  const getAttendanceSummary = () => {
    const presentStudents: { name: string }[] = [];
    const absentStudents: { name: string }[] = [];

    students?.forEach(student => {
      const existingAttendance = dateAttendance.find(a => a.student_id === student.student_id);
      const status = attendanceState[student.student_id] || existingAttendance?.status || "Present";
      // Use calling_name first, fallback to first_name
      const name = student.student?.calling_name || student.student?.first_name || "Unknown";

      if (status === "Present") {
        presentStudents.push({ name });
      } else {
        absentStudents.push({ name });
      }
    });

    return { presentStudents, absentStudents };
  };

  const handleMarkTeachingComplete = async () => {
    if (!id) return;

    try {
      await updateClass.mutateAsync({
        classId: id,
        updates: {
          status: "Teaching Complete",
          teaching_completed_date: format(new Date(), "yyyy-MM-dd"),
        },
      });
      toast.success("Class marked as teaching complete");
      setShowMarkComplete(false);
    } catch (error) {
      toast.error("Failed to update class");
    }
  };

  const handleAddStudents = async () => {
    if (!id || selectedNewStudents.length === 0) return;

    try {
      await addStudentsToClass.mutateAsync({
        classId: id,
        studentIds: selectedNewStudents,
      });
      toast.success(`${selectedNewStudents.length} student(s) added to class`);
      setShowAddStudents(false);
      setSelectedNewStudents([]);
    } catch (error) {
      toast.error("Failed to add students");
    }
  };

  const handleSetExamApplicationDate = async (studentClassId: string) => {
    if (!examApplicationDate) {
      toast.error("Please select a date");
      return;
    }

    try {
      await updateStudentProgress.mutateAsync({
        studentClassId,
        updates: { exam_application_date: examApplicationDate },
      });
      toast.success("Exam application date saved");
      setShowExamDateDialog(null);
      setExamApplicationDate("");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleUpdateStudentResult = async (studentClassId: string, result: string) => {
    try {
      const updates: any = { exam_result: result };
      if (result === "Fail") {
        updates.exam_fail_date = format(new Date(), "yyyy-MM-dd");
      } else if (result === "Pass") {
        updates.subject_status = "Completed";
        updates.subject_completion_date = format(new Date(), "yyyy-MM-dd");
      }

      await updateStudentProgress.mutateAsync({ studentClassId, updates });
      toast.success("Student progress updated");

      // Check if all students passed
      const allPassed = students?.every(s => 
        s.id === studentClassId ? result === "Pass" : s.exam_result === "Pass"
      );

      if (allPassed && classData?.status === "Teaching Complete") {
        await updateClass.mutateAsync({
          classId: id!,
          updates: {
            status: "Closed",
            all_passed_date: format(new Date(), "yyyy-MM-dd"),
          },
        });
        toast.success("All students passed! Class closed.");
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active": return "bg-green-500/10 text-green-500 border-green-500/20";
      case "Teaching Complete": return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "Closed": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getAttendanceColor = (percentage: number | null) => {
    if (percentage === null) return "text-muted-foreground";
    if (percentage >= 80) return "text-green-500";
    if (percentage >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  if (classLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!classData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6">
          <p>Class not found</p>
        </div>
      </Layout>
    );
  }

  // Get attendance for selected date
  const dateAttendance = attendanceRecords?.filter(a => a.class_date === attendanceDate) || [];
  const hasAttendanceForDate = dateAttendance.length > 0;

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/subject-classes")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{classData.class_name}</h1>
              <Badge variant="outline" className={getStatusColor(classData.status)}>
                {classData.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {classData.subject?.name} • {classData.instructor?.name}
            </p>
          </div>

          <div className="flex gap-2">
            {/* Add Students Dialog */}
            <Dialog open={showAddStudents} onOpenChange={setShowAddStudents}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Students
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Students to Class</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Label>Select Students</Label>
                  <div className="border rounded-md max-h-64 overflow-y-auto p-2 space-y-1">
                    {availableStudents && availableStudents.length > 0 ? (
                      availableStudents.map(student => (
                        <label key={student.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedNewStudents.includes(student.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedNewStudents([...selectedNewStudents, student.id]);
                              } else {
                                setSelectedNewStudents(selectedNewStudents.filter(id => id !== student.id));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">
                            {student.first_name} {student.last_name}
                            {student.calling_name && ` (${student.calling_name})`}
                          </span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        All students are already in this class
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedNewStudents.length} student(s) selected
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowAddStudents(false)}>Cancel</Button>
                    <Button 
                      onClick={handleAddStudents} 
                      disabled={selectedNewStudents.length === 0 || addStudentsToClass.isPending}
                    >
                      {addStudentsToClass.isPending ? "Adding..." : "Add Students"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Only instructors can mark teaching complete */}
            {classData.status === "Active" && canMarkComplete && (
              <Dialog open={showMarkComplete} onOpenChange={setShowMarkComplete}>
                <DialogTrigger asChild>
                  <Button variant="outline">Mark Teaching Complete</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Mark Teaching Complete?</DialogTitle>
                  </DialogHeader>
                  <p className="text-muted-foreground">
                    This will mark the teaching portion as complete. You can still track exam results until all students pass.
                  </p>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setShowMarkComplete(false)}>Cancel</Button>
                    <Button onClick={handleMarkTeachingComplete}>Confirm</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{students?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Students</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{classData.total_class_days}</p>
                  <p className="text-xs text-muted-foreground">Class Days</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {students?.filter(s => s.exam_result === "Pass").length || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Passed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">
                    {students?.length 
                      ? Math.round(
                          (students.reduce((acc, s) => acc + (s.attendance_percentage || 0), 0) / students.length)
                        )
                      : 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Avg Attendance</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="attendance" className="space-y-4">
          <TabsList>
            <TabsTrigger value="attendance">
              {isManager ? "Mark Absences" : "Mark Attendance"}
            </TabsTrigger>
            <TabsTrigger value="students">Students</TabsTrigger>
            <TabsTrigger value="history">Attendance History</TabsTrigger>
            {(isAdminOrAbove || isManager) && (
              <TabsTrigger value="absence-reasons">Absence Reasons</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="attendance">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{isManager ? "Mark Absences" : "Mark Attendance"}</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={attendanceDate}
                      onChange={(e) => {
                        setAttendanceDate(e.target.value);
                        setAttendanceMarkedSuccess(false);
                      }}
                      className="w-auto"
                      max={format(new Date(), "yyyy-MM-dd")}
                      min={classData.start_date}
                    />
                    {attendanceMarkedSuccess && (
                      <Button variant="outline" size="sm" onClick={handleDownloadAttendance}>
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isManager && (
                  <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-sm text-blue-600">
                      As a manager, you can only mark students as absent. All students are marked present by default.
                    </p>
                  </div>
                )}

                {hasAttendanceForDate && (
                  <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Attendance already marked for this date. Saving will update the records.
                    </p>
                    <Button variant="outline" size="sm" onClick={handleDownloadAttendance}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                )}

                {/* Attendance marking section */}
                <div className="bg-background p-4 rounded-lg">
                  {studentsLoading ? (
                    <div className="space-y-2">
                      {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {students?.map(student => {
                        const existingAttendance = dateAttendance.find(a => a.student_id === student.student_id);
                        const currentStatus = attendanceState[student.student_id] || existingAttendance?.status || "Present";

                        return (
                          <div
                            key={student.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              currentStatus === "Present" ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"
                            }`}
                          >
                            <div>
                              <p className="font-medium">
                                {student.student?.first_name} {student.student?.last_name}
                              </p>
                              {student.student?.calling_name && (
                                <p className="text-sm text-muted-foreground">{student.student.calling_name}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              {canMarkFullAttendance ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant={currentStatus === "Present" ? "default" : "outline"}
                                    className={currentStatus === "Present" ? "bg-green-600 hover:bg-green-700" : ""}
                                    onClick={() => setAttendanceState({ ...attendanceState, [student.student_id]: "Present" })}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Present
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={currentStatus === "Absent" ? "default" : "outline"}
                                    className={currentStatus === "Absent" ? "bg-red-600 hover:bg-red-700" : ""}
                                    onClick={() => setAttendanceState({ ...attendanceState, [student.student_id]: "Absent" })}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Absent
                                  </Button>
                                </>
                              ) : (
                                // Managers can only mark absent
                                <Button
                                  size="sm"
                                  variant={currentStatus === "Absent" ? "default" : "outline"}
                                  className={currentStatus === "Absent" ? "bg-red-600 hover:bg-red-700" : ""}
                                  onClick={() => setAttendanceState({ 
                                    ...attendanceState, 
                                    [student.student_id]: currentStatus === "Absent" ? "Present" : "Absent" 
                                  })}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  {currentStatus === "Absent" ? "Marked Absent" : "Mark Absent"}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-between items-center">
                  {canMarkFullAttendance && (
                    <Button variant="outline" onClick={initializeAttendance}>
                      Mark All Present
                    </Button>
                  )}
                  {isManager && <div />}
                  <Button onClick={handleMarkAttendance} disabled={markAttendance.isPending}>
                    {markAttendance.isPending ? "Saving..." : "Save Attendance"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="students">
            <Card>
              <CardHeader>
                <CardTitle>Student Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {students?.map(student => {
                    // Calculate 45-day reappear countdown for failed students
                    const getReappearCountdown = () => {
                      if (student.exam_result !== "Fail" || !student.exam_fail_date) return null;
                      const failDate = new Date(student.exam_fail_date);
                      const reappearDate = new Date(failDate);
                      reappearDate.setDate(reappearDate.getDate() + 45);
                      const today = new Date();
                      const daysRemaining = Math.ceil((reappearDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                      return { daysRemaining, reappearDate };
                    };
                    
                    const countdown = getReappearCountdown();
                    
                    return (
                      <div key={student.id} className="flex items-center justify-between p-4 rounded-lg border">
                        <div className="flex-1">
                          <p className="font-medium">
                            {student.student?.first_name} {student.student?.last_name}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            <span className={getAttendanceColor(student.attendance_percentage)}>
                              {student.attendance_percentage?.toFixed(1) || 0}% attendance
                            </span>
                            <span className="text-muted-foreground">
                              {student.attendance_count} / {classData.total_class_days} days
                            </span>
                          </div>
                          {student.exam_application_date && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Exam booked: {format(new Date(student.exam_application_date), "MMM d, yyyy")}
                            </p>
                          )}
                          {/* 45-day reappear countdown for failed students */}
                          {countdown && (
                            <div className={`mt-2 p-2 rounded text-xs ${
                              countdown.daysRemaining <= 0 
                                ? "bg-green-500/10 text-green-600" 
                                : countdown.daysRemaining <= 7 
                                  ? "bg-yellow-500/10 text-yellow-600"
                                  : "bg-red-500/10 text-red-600"
                            }`}>
                              {countdown.daysRemaining <= 0 ? (
                                <span>✓ Can reappear for exam (45 days completed)</span>
                              ) : (
                                <span>⏱ {countdown.daysRemaining} days until reappear (Eligible: {format(countdown.reappearDate, "MMM d, yyyy")})</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Exam Application Date Button */}
                          {!student.exam_result && canEditExamResults && (
                            <Dialog 
                              open={showExamDateDialog === student.id} 
                              onOpenChange={(open) => !open && setShowExamDateDialog(null)}
                            >
                              <DialogTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setShowExamDateDialog(student.id);
                                    setExamApplicationDate(student.exam_application_date || "");
                                  }}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  {student.exam_application_date ? "Edit Exam Date" : "Book Exam"}
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Set Exam Application Date</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div>
                                    <Label>Exam Application Date</Label>
                                    <Input
                                      type="date"
                                      value={examApplicationDate}
                                      onChange={(e) => setExamApplicationDate(e.target.value)}
                                    />
                                  </div>
                                  <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setShowExamDateDialog(null)}>Cancel</Button>
                                    <Button onClick={() => handleSetExamApplicationDate(student.id)}>
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}

                          {/* Exam Result */}
                          {student.exam_result === "Pass" ? (
                            <Badge className="bg-green-500">Passed</Badge>
                          ) : student.exam_result === "Fail" ? (
                            <div className="flex flex-col items-end gap-1">
                              <Badge variant="destructive">Failed</Badge>
                              {canEditExamResults && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-xs h-6"
                                  onClick={() => handleUpdateStudentResult(student.id, "Pass")}
                                >
                                  Mark Pass
                                </Button>
                              )}
                            </div>
                          ) : canEditExamResults ? (
                            <Select
                              value={student.exam_result || ""}
                              onValueChange={(value) => handleUpdateStudentResult(student.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue placeholder="Exam Result" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Pass">Pass</SelectItem>
                                <SelectItem value="Fail">Fail</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Attendance History</CardTitle>
              </CardHeader>
              <CardContent>
                {attendanceRecords && attendanceRecords.length > 0 ? (
                  <div className="space-y-4">
                    {Array.from(new Set(attendanceRecords.map(a => a.class_date))).map(date => {
                      const dateRecords = attendanceRecords.filter(a => a.class_date === date);
                      const presentCount = dateRecords.filter(a => a.status === "Present").length;
                      const absentCount = dateRecords.filter(a => a.status === "Absent").length;

                      return (
                        <div key={date} className="p-4 rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium">{format(new Date(date), "EEEE, MMM d, yyyy")}</p>
                            <div className="flex gap-2">
                              <Badge variant="outline" className="bg-green-500/10 text-green-500">
                                {presentCount} Present
                              </Badge>
                              {absentCount > 0 && (
                                <Badge variant="outline" className="bg-red-500/10 text-red-500">
                                  {absentCount} Absent
                                </Badge>
                              )}
                            </div>
                          </div>
                          {absentCount > 0 && (
                            <div className="text-sm text-muted-foreground">
                              Absent: {dateRecords
                                .filter(a => a.status === "Absent")
                                .map(a => {
                                  const student = students?.find(s => s.student_id === a.student_id);
                                  return student?.student?.first_name;
                                })
                                .join(", ")}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No attendance records yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Absence Reasons Tab - Only visible to Admin/Manager */}
          {(isAdminOrAbove || isManager) && (
            <TabsContent value="absence-reasons">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Absence Reasons
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {absenceNotes && absenceNotes.length > 0 ? (
                    <div className="space-y-4">
                      {absenceNotes.map((note: any) => (
                        <div key={note.id} className="p-4 rounded-lg border">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">
                                {note.student?.first_name} {note.student?.last_name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(note.absence_date), "MMM d, yyyy")}
                              </p>
                            </div>
                            <Badge variant="outline">
                              <Phone className="h-3 w-3 mr-1" />
                              {note.contacted_person}
                            </Badge>
                          </div>
                          <div className="mt-2">
                            <p className="text-sm">
                              <span className="font-medium">Reason:</span> {note.reason}
                            </p>
                            {note.notes && (
                              <p className="text-sm text-muted-foreground mt-1">
                                <span className="font-medium">Notes:</span> {note.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No absence reasons recorded for this class
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Hidden styled download section - matches reference image */}
        <div 
          ref={downloadRef} 
          style={{ display: 'none', position: 'absolute', left: '-9999px', width: '500px' }}
        >
          <div style={{ backgroundColor: '#ffffff', padding: '32px', fontFamily: 'system-ui, sans-serif' }}>
            {/* Header with logo and title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '16px', borderBottom: '3px solid #dc2626', marginBottom: '24px' }}>
              <img src={logoImage} alt="Skylink Aviation" style={{ height: '48px' }} />
              <div style={{ textAlign: 'right' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111', margin: 0 }}>Skylink Aviation</h2>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626', margin: 0 }}>Attendance Report</h3>
              </div>
            </div>
            
            {/* Info box */}
            <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Instructor</p>
                  <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#111', margin: 0 }}>{classData?.instructor?.name?.toUpperCase()}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Subject</p>
                  <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#111', margin: 0 }}>{classData?.subject?.name}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Class</p>
                  <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#111', margin: 0 }}>{classData?.class_name}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Date & Time</p>
                  <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#111', margin: 0 }}>{format(new Date(attendanceDate), "dd/MM/yyyy")}, {new Date().toLocaleTimeString()}</p>
                </div>
              </div>
            </div>

            {/* Present section */}
            <div style={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #22c55e', padding: '16px 20px', borderRadius: '4px', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#22c55e', margin: '0 0 12px 0' }}>
                ✓ Present ({getAttendanceSummary().presentStudents.length})
              </h4>
              <div>
                {getAttendanceSummary().presentStudents.map((student, idx) => (
                  <p key={idx} style={{ fontSize: '14px', color: '#111', margin: '4px 0', paddingLeft: '8px' }}>• {student.name}</p>
                ))}
                {getAttendanceSummary().presentStudents.length === 0 && (
                  <p style={{ fontSize: '14px', color: '#666', margin: '4px 0', paddingLeft: '8px', fontStyle: 'italic' }}>No students</p>
                )}
              </div>
            </div>

            {/* Absent section */}
            <div style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444', padding: '16px 20px', borderRadius: '4px', marginBottom: '24px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#ef4444', margin: '0 0 12px 0' }}>
                ✗ Absent ({getAttendanceSummary().absentStudents.length})
              </h4>
              <div>
                {getAttendanceSummary().absentStudents.map((student, idx) => (
                  <p key={idx} style={{ fontSize: '14px', color: '#111', margin: '4px 0', paddingLeft: '8px' }}>• {student.name}</p>
                ))}
                {getAttendanceSummary().absentStudents.length === 0 && (
                  <p style={{ fontSize: '14px', color: '#666', margin: '4px 0', paddingLeft: '8px', fontStyle: 'italic' }}>No students</p>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: '16px', textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Generated on {new Date().toLocaleString()}</p>
              <p style={{ fontSize: '10px', color: '#999', margin: 0 }}>Skylink Ground School Portal</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SubjectClassDetails;